import React, { useState, useEffect, useRef } from 'react';
import { Skeleton } from 'antd';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import { primaryShades } from '../../theme/theme';

const COLORS = {
    modelIconBackground: primaryShades.primary100,
    modelIconDark: primaryShades.primary700,
    navigationIcon: primaryShades.primary300,
    backgroundTransparent: 'transparent',
};

const SIZES = {
    spacingXs: '0.25rem',
    radiusSmall: '0.25rem',
    borderMedium: '0.125rem',
    fontLarge: '2rem',
    thumbnailSize: '3.75rem',
};

const LazyThumbnail = ({
    image,
    index,
    isSelected,
    onClick,
    processedImages,
    errorImages,
    onImageProcess
}) => {
    const [isLoading, setIsLoading] = useState(!image.is3DModel);
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef(null);
    const observerRef = useRef(null);

    // Intersection Observer per lazy loading
    useEffect(() => {
        if (image.is3DModel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Inizia il caricamento dell'immagine
                        loadImage();
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                rootMargin: '50px', // Inizia il caricamento 50px prima che diventi visibile
                threshold: 0.1
            }
        );

        if (imgRef.current) {
            observer.observe(imgRef.current);
            observerRef.current = observer;
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [image.is3DModel]);

    const loadImage = () => {
        if (image.is3DModel || processedImages[image.key] || errorImages[image.key]) {
            setIsLoading(false);
            return;
        }

        // Crea una nuova immagine per il preload
        const img = new Image();

        img.onload = () => {
            // Processa l'immagine usando la funzione del parent
            onImageProcess(image.originalUrl || image.src, image.key);
            setIsLoading(false);
            setHasError(false);
        };

        img.onerror = () => {
            setIsLoading(false);
            setHasError(true);
        };

        img.src = image.originalUrl || image.src;
    };

    // Effetto per gestire le immagini già processate
    useEffect(() => {
        if (!image.is3DModel && (processedImages[image.key] || errorImages[image.key])) {
            setIsLoading(false);
            setHasError(!!errorImages[image.key]);
        }
    }, [processedImages, errorImages, image.key, image.is3DModel]);

    const renderContent = () => {
        if (image.is3DModel) {
            return (
                <ThreeDRotationIcon
                    style={{
                        fontSize: SIZES.fontLarge,
                        color: COLORS.modelIconDark
                    }}
                />
            );
        }

        if (isLoading) {
            return (
                <Skeleton.Image
                    active
                />
            );
        }

        if (hasError) {
            return (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '12px'
                }}>
                    Error
                </div>
            );
        }

        return (
            <img
                src={processedImages[image.key] || image.src}
                alt={`Thumbnail ${index}`}
                style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                }}
                onError={() => setHasError(true)}
            />
        );
    };

    return (
        <div
            ref={imgRef}
            onClick={onClick}
            style={{
                width: SIZES.thumbnailSize,
                height: SIZES.thumbnailSize,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: SIZES.radiusSmall,
                overflow: 'hidden',
                border: isSelected
                    ? `${SIZES.borderMedium} solid ${COLORS.navigationIcon}`
                    : `${SIZES.borderMedium} solid transparent`,
                opacity: isSelected ? 1 : 0.7,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: SIZES.spacingXs,
                backgroundColor: image.is3DModel
                    ? COLORS.modelIconBackground
                    : COLORS.backgroundTransparent
            }}
        >
            {renderContent()}
        </div>
    );
};

export default LazyThumbnail; 